import type { TicketStatus } from '@brimo/shared';
import { Select, Tooltip } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import type { TicketSegment } from '../types';
import styles from './detail.module.css';

export interface StatusSegmentsProps {
  segments: TicketSegment[];
  pending: boolean;
  onChange: (to: TicketStatus) => void;
}

/**
 * The status switch of Backoffice 05–08 (Neu · In Arbeit · Wartet auf Kunde · Gelöst · Geschlossen). The server
 * decides per segment (`allowed`, `reason`); a refused segment stays visible and explains itself in a tooltip. Below
 * 768 px the switch is a select.
 */
export function StatusSegments({ segments, pending, onChange }: StatusSegmentsProps) {
  const active = segments.find((segment) => segment.active);
  return (
    <>
      <div role="radiogroup" aria-label="Status" className={styles.segments}>
        {segments.map((segment) => {
          const disabled = !segment.allowed && !segment.active;
          const button = (
            <button
              key={segment.key}
              type="button"
              role="radio"
              aria-checked={segment.active}
              aria-disabled={disabled || pending || undefined}
              className={clsx('pressable', styles.segment, segment.active && styles.segmentActive)}
              onClick={() => {
                if (segment.active || disabled || pending) return;
                onChange(segment.key);
              }}
            >
              {segment.label}
            </button>
          );
          return disabled && segment.reason ? (
            <Tooltip key={segment.key} content={segment.reason} placement="bottom">
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>
      <Select
        label="Status"
        labelHidden
        className={styles.segmentSelect}
        value={active?.key ?? ''}
        disabled={pending}
        onChange={(event) => {
          const next = segments.find((segment) => segment.key === event.target.value);
          if (next && !next.active && next.allowed) onChange(next.key);
        }}
        options={segments.map((segment) => ({
          value: segment.key,
          label: segment.label,
          disabled: !segment.allowed && !segment.active,
        }))}
      />
    </>
  );
}
